import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { 
  Flex, Text, Card, Box, Badge, Button, Grid,
  Select, Tooltip, Separator,
  IconButton, ScrollArea, Checkbox, Dialog, Progress
} from "@radix-ui/themes";
import { 
  StarFilledIcon,
  GearIcon,
} from "@radix-ui/react-icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ToastAlert } from "components";
import { useProfileContext } from "context";
import { useToast } from "../../context/ToastContext";
import { useSurveyResponseStats } from "hooks/bSurveys/useSurveyResponses";
import { getDashboardSettings, updateDashboardSettings } from "api/bSurveysApi";

// =============================================================================
// TRUNCATED TEXT COMPONENT
// =============================================================================

const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };
    
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);
  
  const textElement = (
    <Box as="div"
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Text size={size} weight={weight} color={color}>{text}</Text>
    </Box>
  );
  
  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

// =============================================================================
// AGGREGATED INSIGHTS COMPONENT
// =============================================================================

function AggregatedInsights({ stats, isLoading, selectedQuestions }) {
  const questionStats = useMemo(() => {
    if (!stats?.question_stats) return [];
    
    return stats.question_stats
      .filter(q => q.block_type !== 'marketingConsent' && selectedQuestions.includes(q.question))
      .map(q => {
        const stat = {
          question: q.question,
          blockType: q.block_type,
          answerCount: q.answer_count,
        };

        if (q.block_type === 'rating' || q.block_type === 'experienceRating') {
          stat.avgRating = q.avg_rating;
          stat.ratingDistribution = [0, 0, 0, 0, 0];
          if (q.distribution) {
            Object.entries(q.distribution).forEach(([rating, count]) => {
              stat.ratingDistribution[parseInt(rating) - 1] = count;
            });
          }
        } else if (q.block_type === 'yesNo' || q.block_type === 'recommendToFriend') {
          stat.yesCount = q.yes_count;
          stat.noCount = q.no_count;
          stat.yesPercentage = q.yes_percentage;
        } else if (q.block_type === 'multipleChoice' || q.block_type === 'howDidYouFindUs' || q.block_type === 'checkbox') {
          stat.distribution = q.distribution;
        } else if (q.block_type === 'shortAnswer' || q.block_type === 'whatCanWeImprove') {
          stat.textAnswers = q.text_answers;
        }

        return stat;
      });
  }, [stats, selectedQuestions]);

  if (isLoading) {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ height: '200px' }}>
        <Box style={{ width: '160px' }}>
          <Progress size="1" duration="1" />
        </Box>
        <Text size="2" color="gray">Loading analytics...</Text>
      </Flex>
    );
  }

  if (!stats || stats.total_responses === 0) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        justify="center" 
        style={{ 
          height: '100%',
          border: '1px dashed var(--gray-a6)',
          borderRadius: 'var(--radius-4)',
          alignSelf: 'stretch',
          width: '100%',
        }}
      >
        <Text color="gray">No responses found for the selected filter.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="5">
      {/* Top Metrics Row */}
      <Grid columns="3" gap="4" style={{ width: "100%" }}>
        {/* Total Responses Card */}
        <Card>
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium" color="gray">Total responses</Text>
            <Text size="6" weight="bold">{stats.total_responses}</Text>
            <Text size="1" weight="regular" color="gray">Across all surveys</Text>
          </Flex>
        </Card>

        {/* Response Trend Card */}
        <Card>
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium" color="gray">This week</Text>
            <Flex align="baseline" gap="2">
              <Text size="6" weight="bold">{stats.this_week_count}</Text>
              {stats.trend_percentage !== 0 && (
                <Badge 
                  color={stats.trend_percentage > 0 ? 'green' : 'red'} 
                  variant="soft"
                  size="1"
                >
                  {stats.trend_percentage > 0 ? '↑' : '↓'} {Math.abs(stats.trend_percentage)}%
                </Badge>
              )}
            </Flex>
            <Text size="1" color="gray" weight="regular">
              vs {stats.last_week_count} last week
            </Text>
          </Flex>
        </Card>

        {/* Average Rating Card */}
        {stats.avg_rating && (
          <Card>
            <Flex direction="column" gap="2">
              <Text size="2" color="gray" weight="medium">Avg. Rating</Text>
              <Flex align="baseline" gap="2">
                <Text size="6" weight="bold">{stats.avg_rating}</Text>
                <Text size="3" color="gray">/5</Text>
                {stats.rating_trend_percentage !== undefined && stats.rating_trend_percentage !== 0 && (
                  <Badge 
                    color={stats.rating_trend_percentage > 0 ? 'green' : 'red'} 
                    variant="soft"
                    size="1"
                  >
                    {stats.rating_trend_percentage > 0 ? '↑' : '↓'} {Math.abs(stats.rating_trend_percentage)}%
                  </Badge>
                )}
              </Flex>
              <Flex align="center" gap="1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarFilledIcon 
                    key={star} 
                    width={12} 
                    height={12}
                    style={{ color: star <= Math.round(parseFloat(stats.avg_rating)) ? '#f59e0b' : 'var(--gray-a5)' }}
                  />
                ))}
                <Text size="1" color="gray" ml="1">from {stats.rating_count} ratings</Text>
              </Flex>
            </Flex>
          </Card>
        )}
      </Grid>

      {/* Selected Questions Stats */}
      {questionStats.length === 0 ? (
        <Flex direction="column" align="center" justify="center" gap="2" p="6"
          style={{ border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)' }}>
          <Text color="gray">No questions selected for the dashboard.</Text>
          <Text size="1" color="gray">Click the gear icon above to select questions to track.</Text>
        </Flex>
      ) : (
        <Grid columns="3" gap="4" style={{ width: "100%", alignItems: 'stretch' }}>
          {questionStats.map((stat, index) => (
            <QuestionStatCard key={index} stat={stat} totalResponses={stats.total_responses} />
          ))}
        </Grid>
      )}
    </Flex>
  );
}

// =============================================================================
// QUESTION STAT CARD
// =============================================================================

function QuestionStatCard({ stat, totalResponses }) {
  const { question, blockType, answerCount } = stat;

  return (
    <Card style={{ height: '100%', minHeight: '240px' }}>
      <Flex direction="column" gap="3" style={{ height: '100%' }}>
        <Flex direction="column" gap="1">
          <TruncatedText size="3" weight="medium">{question}</TruncatedText>
          <Text size="1" color="gray">{answerCount} response{answerCount !== 1 ? 's' : ''}</Text>
        </Flex>
        
        <Separator size="4" />

        {/* Rating display */}
        {(blockType === 'rating' || blockType === 'experienceRating') && stat.avgRating && (
          <Flex direction="column" gap="3">
            <Flex align="center" gap="2">
              <Text size="6" weight="bold">{stat.avgRating}</Text>
              <Text size="2" color="gray">/5 average</Text>
            </Flex>
            <Flex direction="column" gap="1">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = stat.ratingDistribution[rating - 1];
                const percentage = answerCount > 0 ? (count / answerCount) * 100 : 0;
                return (
                  <Flex key={rating} align="center" gap="2">
                    <Flex align="center" gap="1" style={{ width: '40px' }}>
                      <Text size="1">{rating}</Text>
                      <StarFilledIcon width={10} height={10} style={{ color: '#f59e0b' }} />
                    </Flex>
                    <Box style={{ flex: 1, height: '12px', backgroundColor: 'var(--gray-a3)', borderRadius: '2px', overflow: 'hidden' }}>
                      <Box style={{ 
                        height: '100%', 
                        width: `${percentage}%`,
                        backgroundColor: rating >= 4 ? 'var(--green-9)' : rating === 3 ? 'var(--amber-9)' : 'var(--red-9)',
                        borderRadius: '2px',
                      }} />
                    </Box>
                    <Text size="1" color="gray" style={{ width: '30px', textAlign: 'right' }}>{count}</Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        )}

        {/* Yes/No display */}
        {(blockType === 'yesNo' || blockType === 'recommendToFriend') && (
          <Flex direction="column" gap="3" style={{ flex: 1 }}>
            <Flex align="baseline" gap="1">
              <Text size="6" weight="bold" style={{ 
                color: stat.yesPercentage >= 70 ? 'var(--green-11)' : stat.yesPercentage >= 50 ? 'var(--amber-11)' : 'var(--red-11)' 
              }}>
                {stat.yesPercentage}%
              </Text>
              <Text size="2" color="gray">said yes</Text>
            </Flex>
            {/* Large Yes/No boxes */}
            <Flex gap="3" style={{ flex: 1 }}>
              <Box 
                p="3" 
                style={{ 
                  flex: 1, 
                  backgroundColor: 'var(--green-a3)', 
                  borderRadius: 'var(--radius-3)',
                  border: '1px solid var(--green-a6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '70px',
                }}
              >
                <Text size="5" weight="bold" style={{ color: 'var(--green-11)' }}>{stat.yesCount}</Text>
                <Text size="2" color="gray">Yes</Text>
              </Box>
              <Box 
                p="3" 
                style={{ 
                  flex: 1, 
                  backgroundColor: 'var(--red-a3)', 
                  borderRadius: 'var(--radius-3)',
                  border: '1px solid var(--red-a6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '70px',
                }}
              >
                <Text size="5" weight="bold" style={{ color: 'var(--red-11)' }}>{stat.noCount}</Text>
                <Text size="2" color="gray">No</Text>
              </Box>
            </Flex>
          </Flex>
        )}

        {/* Multiple choice / How did you find us distribution */}
        {(blockType === 'multipleChoice' || blockType === 'howDidYouFindUs' || blockType === 'checkbox') && stat.distribution && (
          <Flex direction="column" gap="2" style={{ minHeight: '140px' }}>
            {Object.entries(stat.distribution)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 6)
              .map(([option, count]) => {
                const percentage = (count / answerCount) * 100;
                return (
                  <Flex key={option} justify="between" align="center">
                    <Badge variant="soft" size="1">{option}</Badge>
                    <Text size="1" color="gray">{count} ({percentage.toFixed(0)}%)</Text>
                  </Flex>
                );
              })}
          </Flex>
        )}

        {/* Text answers */}
        {(blockType === 'shortAnswer' || blockType === 'whatCanWeImprove') && stat.textAnswers && (
          <ScrollArea style={{ maxHeight: '150px' }}>
            <Flex direction="column" gap="2">
              {stat.textAnswers.map((text, i) => (
                <Box 
                  key={i} 
                  p="2" 
                  style={{ 
                    backgroundColor: 'var(--gray-a2)', 
                    borderRadius: 'var(--radius-2)',
                    borderLeft: '2px solid var(--accent-9)',
                  }}
                >
                  <Text size="1" style={{ fontStyle: 'italic' }}>"{text}"</Text>
                </Box>
              ))}
            </Flex>
          </ScrollArea>
        )}
      </Flex>
    </Card>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SurveyAnalytics() {
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const organisationId = userProfile?.organisation_id;
  const queryClient = useQueryClient();

  const [selectedTemplate, setSelectedTemplate] = useState("all");

  // User-selected questions for dashboard
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [tempSelectedQuestions, setTempSelectedQuestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch saved dashboard settings
  const { data: dashboardSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ["surveyDashboardSettings", organisationId],
    queryFn: () => getDashboardSettings(organisationId).then((res) => res.data),
    enabled: !!organisationId,
  });

  // Mutation to save settings
  const saveSettingsMutation = useMutation({
    mutationFn: (data) => updateDashboardSettings(organisationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["surveyDashboardSettings", organisationId]);
    },
  });

  // Fetch responses stats (includes templates with minimal data)
  const { 
    data: statsData, 
    isLoading: isLoadingStats 
  } = useSurveyResponseStats(
    organisationId, 
    selectedTemplate !== 'all' ? selectedTemplate : null
  );

  // Extract available questions from stats for the question picker
  const allQuestions = useMemo(() => {
    if (!statsData?.question_stats) return [];
    // Exclude marketingConsent from dashboard question selection and stats
    return statsData.question_stats
      .filter(q => q.block_type !== 'marketingConsent')
      .map(q => ({
        question: q.question,
        block_type: q.block_type,
        count: q.answer_count,
      }));
  }, [statsData]);

  const availableQuestionSet = useMemo(
    () => new Set(allQuestions.map((q) => q.question)),
    [allQuestions]
  );

  const sanitizeSelectedQuestions = useCallback(
    (questions) => {
      const source = Array.isArray(questions) ? questions : [];
      const uniqueVisibleQuestions = [];

      source.forEach((question) => {
        if (!availableQuestionSet.has(question)) {
          return;
        }
        if (!uniqueVisibleQuestions.includes(question)) {
          uniqueVisibleQuestions.push(question);
        }
      });

      return uniqueVisibleQuestions;
    },
    [availableQuestionSet]
  );

  // Initialize selected questions when stats load (default to first 3, or use saved settings)
  useEffect(() => {
    // If we have saved settings, use those
    if (dashboardSettings?.selected_questions?.length > 0) {
      setSelectedQuestions(sanitizeSelectedQuestions(dashboardSettings.selected_questions));
    } else if (allQuestions.length > 0 && selectedQuestions.length === 0) {
      // Otherwise default to first 3 questions
      setSelectedQuestions(allQuestions.slice(0, 3).map(q => q.question));
    }
  }, [allQuestions, dashboardSettings, sanitizeSelectedQuestions, selectedQuestions.length]);

  useEffect(() => {
    if (selectedQuestions.length === 0) {
      return;
    }

    const sanitized = sanitizeSelectedQuestions(selectedQuestions);
    if (sanitized.length !== selectedQuestions.length) {
      setSelectedQuestions(sanitized);
    }
  }, [selectedQuestions, sanitizeSelectedQuestions]);

  // Handle opening the question selector dialog
  const handleOpenQuestionDialog = () => {
    setTempSelectedQuestions(sanitizeSelectedQuestions(selectedQuestions));
    setQuestionDialogOpen(true);
  };

  // Handle toggling a question in the temp selection
  const handleToggleQuestion = (question) => {
    setTempSelectedQuestions(prev => {
      if (prev.includes(question)) {
        return prev.filter(q => q !== question);
      }
      return [...prev, question];
    });
  };

  // Save selected questions
  const handleSaveQuestions = async () => {
    setIsSaving(true);
    try {
      const sanitizedSelection = sanitizeSelectedQuestions(tempSelectedQuestions);
      await saveSettingsMutation.mutateAsync({ selected_questions: sanitizedSelection });
      setSelectedQuestions(sanitizedSelection);
      setQuestionDialogOpen(false);
      setToastMessage("Dashboard settings saved");
    } catch (error) {
      setToastMessage("Failed to save dashboard settings, please try again or contact support.");
    } finally {
      setIsSaving(false);
    }
  };

  // Use templates from stats response (minimal data: template_id, name, primary_color)
  const templates = statsData?.templates || [];
  const isLoading = isLoadingStats;

  if (isLoading && !statsData) {
    return (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  return (
    <>
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
        {/* Header with filters */}
        <Flex justify="between" align="center" wrap="wrap" gap="3" style={{ alignSelf: 'stretch' }}>
          <Text size="2" color="gray">
            Use the settings gear to choose which survey questions appear in these analytics cards.
          </Text>

          <Flex align="center" gap="3">
            {/* Question Customizer */}
            <Tooltip content="Customize dashboard questions">
              <IconButton variant="soft" color="gray" onClick={handleOpenQuestionDialog}>
                <GearIcon width={16} height={16} />
              </IconButton>
            </Tooltip>

            {/* Survey Filter */}
            <Select.Root value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <Select.Trigger placeholder="All surveys" style={{ minWidth: '180px' }} />
              <Select.Content>
                <Select.Item value="all">All surveys</Select.Item>
                <Select.Separator />
                {templates.map(template => (
                  <Select.Item key={template.template_id} value={template.template_id}>
                    <Flex align="center" gap="2">
                      <Box 
                        style={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: template.primary_color 
                        }}
                      />
                      {template.name}
                    </Flex>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>

        {/* Analytics Content - Scrollable */}
        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: 'auto', height: '100%', flex: "1 0 0" }}>
          <AggregatedInsights 
            stats={statsData}
            isLoading={isLoadingStats}
            selectedQuestions={selectedQuestions}
          />
        </Flex>

        {/* Question Customization Dialog */}
        <Dialog.Root open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
          <Dialog.Content style={{ maxWidth: 500 }}>
            <Dialog.Title>Customise Dashboard</Dialog.Title>
            <Dialog.Description size="2" mb="4" color="gray">
              Select questions to display on the analytics dashboard. Questions with more responses appear first.
            </Dialog.Description>

            <Flex direction="column" gap="2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {allQuestions.map((q) => {
                const isSelected = tempSelectedQuestions.includes(q.question);
                
                return (
                  <Flex key={q.question} align="center" gap="3"
                    style={{ borderRadius: 'var(--radius-2)', cursor: 'pointer' }}
                    onClick={() => handleToggleQuestion(q.question)}>
                    <Checkbox 
                      checked={isSelected} 
                      onCheckedChange={() => handleToggleQuestion(q.question)}
                    />
                    <Flex direction="column" gap="0" style={{ flex: 1 }}>
                      <Text size="2">{q.question}</Text>
                      <Text size="1" color="gray">({q.count} responses)</Text>
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>

            <Flex gap="3" mt="4" justify="between" align="center">
              <Text size="1" color="gray">
                {sanitizeSelectedQuestions(tempSelectedQuestions).length} question{sanitizeSelectedQuestions(tempSelectedQuestions).length !== 1 ? 's' : ''} selected
              </Text>
              <Flex gap="3">
                <Dialog.Close>
                  <Button variant="soft" color="gray" disabled={isSaving}>Cancel</Button>
                </Dialog.Close>
                <Button onClick={handleSaveQuestions} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </Flex>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>
      <ToastAlert />
    </>
  );
}
